import Fingerprint2 from "fingerprintjs2";
import FingerprintJS, { type Agent } from "@fingerprintjs/fingerprintjs-pro";

import { logger } from "@jslib/shared/utils/logger";

import { CustomMetricNames, EventName, recordGaEvent } from "../GoogleAnalytics/ga";

import { getOptions } from "../../options/options";

type FingerprintProperties = {
  clientId: string;
  workerEndpoint: string;
  scriptUrl: string;
};

/**
 * @description Gets Fingerprint config from window properties passed in from view-main template.
 *              This avoids env specific logic in jslib.
 */
const getFingerprintProperties = (): FingerprintProperties => {
  const { clientId, workerEndpoint, scriptUrl } = window.FP || {};
  if (
    !clientId ||
    !scriptUrl ||
    !scriptUrl.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//) ||
    !workerEndpoint ||
    !workerEndpoint.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//)
  ) {
    throw new Error("Fingerprint environment not set properly");
  }

  return { clientId, workerEndpoint, scriptUrl };
};

/**
 * @description The user's device fingerprint
 */
let fingerprint: string;

/**
 * @description Get the digital fingerprint hash for a user, based on various digital signals.
 *              Recommended: call this at least 500ms after page ready
 */
export const getFingerprint = async (): Promise<string> => {
  if (!getOptions().useFingerprinting) {
    return Promise.resolve(undefined);
  }

  if (fingerprint) {
    return Promise.resolve(fingerprint);
  }

  return Fingerprint2.getPromise({}).then((components) => {
    const values = components.map((component) => component.value);
    const murmur = Fingerprint2.x64hash128(values.join(""), 31);
    fingerprint = murmur;
    return fingerprint;
  });
};

let fpAgentPromise: Promise<Agent> | null = null;

/**
 * @description Pre-loads the Fingerprint SDK so its available at submit time.
 */
export const loadFingerprintSdk = (): Promise<Agent> => {
  if (fpAgentPromise) {
    return fpAgentPromise;
  }

  fpAgentPromise = (async () => {
    try {
      const { clientId, workerEndpoint, scriptUrl } = getFingerprintProperties();

      return await FingerprintJS.load({
        apiKey: clientId,
        endpoint: [workerEndpoint, FingerprintJS.defaultEndpoint],
        scriptUrlPattern: [
          `${scriptUrl}?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>`,
          FingerprintJS.defaultScriptUrlPattern,
        ],
      });
    } catch (error) {
      // Allow retries on failure
      fpAgentPromise = null;
      logger.error(error, "Failed to load Fingerprint SDK");
      throw error;
    }
  })();

  return fpAgentPromise;
};

const FINGERPRINT_TIMEOUT_MS = 3000;

/**
 * @description Calls Fingerprint to collect device signals and returns the Fingerprint "Request ID".
 *              Returns null if the SDK was not loaded (i.e. fingerprint feature flag is off).
 *              Times out after 3 seconds to avoid blocking form submission.
 */
export const getFingerprintRequestId = async (): Promise<string | null> => {
  if (!fpAgentPromise) {
    return null;
  }

  performance.mark(EventName.FINGERPRINT_ON_SUBMIT);

  const fpResult = Promise.resolve(fpAgentPromise)
    .then((fp) => fp.get())
    .then((result) => result.requestId);

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error("Failed to get Fingerprint results. Request timed out")),
      FINGERPRINT_TIMEOUT_MS,
    ),
  );

  try {
    const requestId = await Promise.race([fpResult, timeout]);
    return requestId;
  } finally {
    const duration = performance.measure(
      EventName.FINGERPRINT_ON_SUBMIT,
      EventName.FINGERPRINT_ON_SUBMIT,
    ).duration;
    recordGaEvent({
      eventName: EventName.FINGERPRINT_ON_SUBMIT,
      params: { [CustomMetricNames.EVENT_DURATION]: duration },
    });
  }
};
